import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { specials } from "@/mocks/specials";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function Home() {
  useSeo({
    title: "欧美大片影视网—最新欧美电影资讯与推荐",
    description:
      "欧美大片网站提供最新最全欧美电影资讯、电影推荐和影评，覆盖各类热门欧美影片，满足用户观看前的信息需求。",
    keywords: "欧美大片,欧美电影,电影资讯,影评推荐,最新电影,热门电影",
  });

  const { isFavorite, toggleFavorite } = useFavorites();

  const latest = [...movies].sort((a, b) => b.year - a.year || b.rating - a.rating).slice(0, 8);
  const popular = [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 8);
  const featuredSpecial = specials[0];

  return (
    <SiteLayout>
      <HeroCarousel />

      {/* category shortcuts */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <SectionHeading
          title="电影分类"
          subtitle="按类型快速找到你想看的欧美电影"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.key}
              to={`/category/${cat.key}`}
              className="group flex flex-col items-center gap-2 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <span className="w-11 h-11 flex items-center justify-center rounded-full bg-background-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                <i className={`${cat.icon} text-xl`} />
              </span>
              <span className="font-heading font-semibold text-sm text-foreground-800">
                {cat.name}
              </span>
              <span className="font-label text-[10px] tracking-wider text-foreground-400 uppercase">
                {cat.en}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* latest */}
      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <SectionHeading
            title="最新上映"
            subtitle="近期值得关注的新片速览"
            moreTo="/category/scifi"
            moreLabel="浏览分类"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {latest.map((movie) => (
              <MovieCard
                key={movie.id}
                movie={movie}
                showExcerpt
                isFavorite={isFavorite(movie.id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        </div>
      </section>

      {/* popular */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <SectionHeading
          title="热门推荐"
          subtitle="基于点击量与影迷评价排序"
          moreTo="/search"
          moreLabel="探索更多"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
          {popular.map((movie, i) => (
            <div key={movie.id} className="relative">
              <span className="absolute -top-2 -left-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 font-heading font-extrabold text-sm">
                {i + 1}
              </span>
              <MovieCard
                movie={movie}
                isFavorite={isFavorite(movie.id)}
                onToggleFavorite={toggleFavorite}
              />
            </div>
          ))}
        </div>
      </section>

      {/* special preview */}
      <section className="w-full bg-primary-950 text-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <span className="w-1 h-8 rounded-full bg-accent-500" />
            <div>
              <h2 className="font-heading font-extrabold text-xl md:text-2xl">
                影评精选专题
              </h2>
              <p className="text-xs md:text-sm text-background-300/80 mt-1">
                深度策划，带你读懂电影背后的故事
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
            <Link
              to={`/special/${featuredSpecial.id}`}
              className="lg:col-span-2 group relative rounded-lg overflow-hidden min-h-[280px] md:min-h-[360px] flex items-end cursor-pointer"
            >
              <img
                src={featuredSpecial.cover}
                alt={`${featuredSpecial.title} 专题配图`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-transparent" />
              <div className="relative p-5 md:p-7">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-label font-bold mb-3">
                  <i className="ri-bookmark-3-fill" /> 本周专题
                </span>
                <h3 className="font-heading font-extrabold text-2xl md:text-3xl mb-2">
                  {featuredSpecial.title}
                </h3>
                <p className="text-sm text-background-200/90 max-w-xl line-clamp-2">
                  {featuredSpecial.intro}
                </p>
              </div>
            </Link>

            <div className="flex flex-col gap-4">
              {specials.slice(1).map((sp) => (
                <Link
                  key={sp.id}
                  to={`/special/${sp.id}`}
                  className="group flex-1 rounded-lg bg-primary-900/70 border border-primary-800 p-5 hover:bg-primary-900 transition-colors cursor-pointer"
                >
                  <span className="font-label text-[11px] tracking-wider text-accent-400 uppercase">
                    {sp.subtitle}
                  </span>
                  <h3 className="font-heading font-bold text-lg mt-2 mb-2">
                    {sp.title}
                  </h3>
                  <p className="text-xs text-background-300/80 leading-relaxed line-clamp-3">
                    {sp.intro}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-400 mt-3">
                    阅读专题
                    <i className="ri-arrow-right-s-line group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* site intro */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-4">
              关于欧美大片影视网
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
              欧美大片影视网是一个专注欧美电影资讯与推荐的影视内容平台。我们汇聚最新影片动态、分类导览、深度影评与热门榜单，帮助影迷在观影前快速了解影片信息，也在观影后找到更多值得回味的作品。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              无论你是想找一部周末放松的喜剧，还是想挑战一部烧脑悬疑，这里都能为你提供清晰的入口与可靠的推荐。
            </p>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
            {[
              { num: "500+", label: "收录影片" },
              { num: "120+", label: "原创影评" },
              { num: "8", label: "电影分类" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg bg-background-50 border border-background-200 p-4 text-center lg:text-left"
              >
                <div className="font-heading font-extrabold text-2xl text-primary-600">
                  {stat.num}
                </div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}